import React, { useMemo, useState } from "react";
import { theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { CheckIcon, PlusIcon } from "@phosphor-icons/react";
import { Button, Grid, IconChip, Modal, Stack } from "@libertify/ui";
import {
  ADDON_CATALOG,
  AddonDef,
  AddonGroup,
  AddonId,
  getConfigItemVisual,
} from "../data/addons";
import {
  Subtitle,
  GroupBlock,
  GroupTitle,
  AddonCard,
  CheckMark,
  CardName,
  CardDescription,
} from "./AddAddonModal.styles";

interface AddAddonModalProps {
  open: boolean;
  onClose: () => void;
  /** Add-ons already in the experience — excluded from the picker. */
  addedIds: AddonId[];
  /** Called with every add-on the user selected, added together. */
  onAdd: (ids: AddonId[]) => void;
}

/**
 * "Add a new add-on" picker: the not-yet-added catalog grouped by category, as
 * multi-select cards. Selecting several and confirming adds them all at once.
 */
export const AddAddonModal: React.FC<AddAddonModalProps> = ({
  open,
  onClose,
  addedIds,
  onAdd,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [selected, setSelected] = useState<Set<AddonId>>(new Set());

  // Available add-ons grouped by category, preserving catalog order (both for
  // the groups themselves and the cards within each).
  const groups = useMemo<[AddonGroup, AddonDef[]][]>(() => {
    const map = new Map<AddonGroup, AddonDef[]>();
    for (const def of ADDON_CATALOG) {
      if (addedIds.includes(def.id)) continue;
      const bucket = map.get(def.group);
      if (bucket) bucket.push(def);
      else map.set(def.group, [def]);
    }
    return [...map.entries()];
  }, [addedIds]);

  const toggle = (id: AddonId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClose = () => {
    setSelected(new Set());
    onClose();
  };

  const handleConfirm = () => {
    onAdd([...selected]);
    handleClose();
  };

  const addDisabled = selected.size === 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="xlarge"
      title={t("createExperience.addAddonModal.title")}
      styles={{
        header: { display: "flex", alignItems: "center", paddingInline: 16 },
        body: { maxHeight: "60vh", overflowY: "auto", paddingInline: 16 },
        footer: { background: token.colorBgContainer, paddingInline: 16 },
      }}
      footer={
        <Stack fullWidth justify="between" align="center">
          <Button variant="outlined" size="m" onClick={handleClose}>
            {t("createExperience.addAddonModal.cancel")}
          </Button>
          <Button
            variant="primary"
            size="m"
            leftIcon={<PlusIcon size={16} />}
            disabled={addDisabled}
            style={
              addDisabled
                ? {
                    background: token.colorTextQuaternary,
                    color: token.colorWhite,
                  }
                : undefined
            }
            onClick={handleConfirm}
          >
            {t("createExperience.addAddonModal.add")}
          </Button>
        </Stack>
      }
    >
      <Subtitle $token={token}>
        {t("createExperience.addAddonModal.subtitle")}
      </Subtitle>

      {groups.map(([group, defs]) => (
        <GroupBlock key={group}>
          <GroupTitle $token={token}>
            {t(`createExperience.addonGroups.${group}`)}
          </GroupTitle>
          <Grid columns={3} gap={10}>
            {defs.map((def) => {
              const { Icon, color, weight } = getConfigItemVisual(def.id);
              const isSelected = selected.has(def.id);
              return (
                <AddonCard
                  key={def.id}
                  $token={token}
                  $selected={isSelected}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => toggle(def.id)}
                >
                  {isSelected && (
                    <CheckMark $token={token}>
                      <CheckIcon size={14} weight="bold" />
                    </CheckMark>
                  )}
                  <IconChip
                    color={color}
                    size="m"
                    icon={<Icon weight={weight} />}
                  />
                  <CardName $token={token}>{t(def.labelKey)}</CardName>
                  <CardDescription $token={token}>
                    {t(def.descriptionKey)}
                  </CardDescription>
                </AddonCard>
              );
            })}
          </Grid>
        </GroupBlock>
      ))}
    </Modal>
  );
};
